import { useTheme } from '@mui/material';
import { memo, useMemo, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DynamicIcon, ShortcutText } from 'src/components';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { appShortcuts } from 'src/constants/appShortcuts';
import {
  canUpdateConversationUrgency,
  useConversationUrgencyOptions,
  useGroupRules,
} from 'src/graphql';
import { useRegisterShortcuts } from 'src/utils/hooks';
import { getThemeColor } from 'src/utils/strings';

type ConversationUrgencyOption = ReturnType<typeof useConversationUrgencyOptions>[number];

const ConversationUrgencyToggleComponent = ({
  name,
  partnerId,
  disabled,
}: {
  name: string;
  partnerId?: number;
  disabled?: boolean;
}) => {
  const { control } = useFormContext();
  const theme = useTheme();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { groupRules } = useGroupRules();
  const isDisabled = Boolean(disabled) || !canUpdateConversationUrgency(groupRules);

  useRegisterShortcuts(
    useMemo(
      () => ({
        ...appShortcuts.openUrgency,
        callback: () => {
          if (isDisabled || document.activeElement === triggerRef.current) {
            return false;
          }
          triggerRef.current?.focus();
          triggerRef.current?.click();
          return false;
        },
      }),
      [isDisabled],
    ),
  );

  const urgencyOptions = useConversationUrgencyOptions(partnerId);
  const options = useMemo<SelectMenuOption[]>(
    () =>
      urgencyOptions.map((option) => ({
        value: option.id,
        title: option.label,
        icon: <DynamicIcon name={option.icon} library={option.iconLibrary} />,
        iconColor: getThemeColor(option.color, theme),
      })),
    [urgencyOptions, theme],
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => (
        <SelectMenu
          ref={triggerRef}
          label="Urgency"
          options={options}
          value={(value as ConversationUrgencyOption | null)?.id ?? null}
          onChange={(selected) =>
            onChange(urgencyOptions.find((option) => option.id === selected))
          }
          hotkey="U"
          variant="inline"
          plainOptionIcons
          disabled={isDisabled}
          sx={{ width: '100%' }}
          tooltip={
            <ShortcutText
              label={appShortcuts.openUrgency.description}
              shortcut={appShortcuts.openUrgency.command}
              size="small"
            />
          }
        />
      )}
    />
  );
};

export const ConversationUrgencyToggle = memo(ConversationUrgencyToggleComponent);
